import './BindEmail.sass';

import React, { FC, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Block, Button, Form, FormItem, Header, Input, ScrollView } from '$uikit';
import { useTranslator } from '$hooks';
import { AuthRequired } from '$core/AuthRequired/AuthRequired';
import { authSelector } from '$store/auth';
import { toastActions } from '$store/toast';
import { mainActions } from '$store/main';
import { dashboardActions } from '$store/dashboard';
import { feedActions } from '$store/feed';
import router from '$navigation/router';
import { goBack, hasHistoryBack } from '$navigation/helper';
import { FEED, SETTINGS } from '$shared/constants/pages';
import { Api } from '$api';

type Step = 'email' | 'code' | 'success';

export const BindEmail: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const { isLogged } = useSelector(authSelector);

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleError = useCallback(
    (error: any) => {
      dispatch(toastActions.setToastFail(error?.message || t('failed_title')));
    },
    [dispatch, t],
  );

  const handleSendCode = useCallback(async () => {
    if (!email.trim()) return;
    setIsLoading(true);
    try {
      await Api.post('/profile/email/bind', { email: email.trim() });
      setStep('code');
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, [email, handleError]);

  const handleVerifyCode = useCallback(async () => {
    if (!code.trim()) return;
    setIsLoading(true);
    try {
      await Api.post('/profile/email/bind/validate', {
        email: email.trim(),
        code: code.trim(),
      });
      dispatch(mainActions.setShouldBindEmail(false));
      dispatch(dashboardActions.loadDashboard());
      dispatch(feedActions.hideNotificationFromUI('bind_email'));
      dispatch(toastActions.setToastSuccess(t('bind_email_success')));
      setStep('success');
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, [email, code, dispatch, t, handleError]);

  if (!isLogged) {
    return <AuthRequired />;
  }

  return (
    <>
      <Header
        showBackButton
        backButtonOnClick={() => (hasHistoryBack() ? goBack() : router.navigate(FEED))}
      >
        {t('bind_email_title')}
      </Header>
      <ScrollView>
        <div className="BindEmail">
          {step === 'email' && (
            <Block>
              <Form>
                <div className="BindEmail__intro">
                  <div>{t('bind_email_description')}</div>
                </div>
                <FormItem title="Email">
                  <Input
                    type="email"
                    placeholder={t('bind_email_input_placeholder')}
                    value={email}
                    onChange={(e: React.ChangeEvent) =>
                      setEmail((e.target as HTMLInputElement).value)
                    }
                  />
                </FormItem>
                <FormItem>
                  <Button onClick={handleSendCode} disabled={isLoading || !email.trim()}>
                    {t('bind_email_send_button')}
                  </Button>
                </FormItem>
              </Form>
            </Block>
          )}
          {step === 'code' && (
            <Block>
              <Form>
                <div className="BindEmail__intro">
                  <div>{t('bind_email_code_description', { email: email.trim() })}</div>
                  <div className="BindEmail__hint">{t('bind_email_or_magic_link')}</div>
                </div>
                <FormItem title={t('bind_email_code_title')}>
                  <Input
                    placeholder={t('bind_email_code_placeholder')}
                    value={code}
                    onChange={(e: React.ChangeEvent) =>
                      setCode((e.target as HTMLInputElement).value)
                    }
                  />
                </FormItem>
                <FormItem>
                  <Button onClick={handleVerifyCode} disabled={isLoading || !code.trim()}>
                    {t('bind_email_confirm_button')}
                  </Button>
                </FormItem>
              </Form>
            </Block>
          )}
          {step === 'success' && (
            <Block>
              <div className="BindEmail__success">
                <div>{t('bind_email_success')}</div>
              </div>
              <FormItem>
                <Button onClick={() => router.navigate(SETTINGS)}>
                  {t('settings_title')}
                </Button>
              </FormItem>
            </Block>
          )}
        </div>
      </ScrollView>
    </>
  );
};
